import React from 'react';
import { motion } from 'framer-motion';
import { LineChart, Line, AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { TrendingUp, Activity, Zap, Clock } from 'lucide-react';

function Analytics() {
  // Sample data for charts
  const usageData = [
    { date: 'Jan 1', queries: 120, apiCalls: 85 },
    { date: 'Jan 5', queries: 250, apiCalls: 165 },
    { date: 'Jan 10', queries: 180, apiCalls: 142 },
    { date: 'Jan 15', queries: 320, apiCalls: 245 },
    { date: 'Jan 20', queries: 290, apiCalls: 220 },
    { date: 'Jan 25', queries: 410, apiCalls: 310 },
    { date: 'Jan 30', queries: 385, apiCalls: 295 }
  ];

  const performanceData = [
    { time: '00:00', responseTime: 45 },
    { time: '04:00', responseTime: 38 },
    { time: '08:00', responseTime: 52 },
    { time: '12:00', responseTime: 68 },
    { time: '16:00', responseTime: 75 },
    { time: '20:00', responseTime: 58 },
    { time: '23:59', responseTime: 42 }
  ];

  const endpointUsage = [
    { endpoint: '/search', calls: 3450 },
    { endpoint: '/optimize', calls: 2890 },
    { endpoint: '/analyze', calls: 2145 },
    { endpoint: '/rank', calls: 1820 },
    { endpoint: '/report', calls: 1260 }
  ];

  const stats = [
    {
      label: 'Avg Response Time',
      value: '52ms',
      change: '-8.2%',
      icon: Clock,
      color: 'from-blue-500 to-cyan-500'
    },
    {
      label: 'Success Rate',
      value: '99.8%',
      change: '+0.3%',
      icon: TrendingUp,
      color: 'from-green-500 to-emerald-500'
    },
    {
      label: 'Active Endpoints',
      value: '12',
      change: '+2',
      icon: Activity,
      color: 'from-purple-500 to-pink-500'
    },
    {
      label: 'Peak Usage',
      value: '410',
      change: '+15.3%',
      icon: Zap,
      color: 'from-orange-500 to-red-500'
    }
  ];

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-dark-800 border border-white/10 rounded-lg p-3">
          <p className="text-white font-medium mb-2">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} className="text-sm" style={{ color: entry.color }}>
              {entry.name}: {entry.value}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="space-y-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <h1 className="text-3xl font-bold text-white mb-2">Usage Analytics</h1>
        <p className="text-gray-400">Monitor your AIRO API performance and usage patterns</p>
      </motion.div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat, index) => (
          <motion.div
            key={stat.label}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="bg-dark-800/50 backdrop-blur-sm border border-white/10 rounded-xl p-6"
          >
            <div className="flex items-center justify-between mb-4">
              <div className={`w-12 h-12 bg-gradient-to-r ${stat.color} rounded-lg flex items-center justify-center opacity-20`}>
                <stat.icon className="w-6 h-6 text-white" />
              </div>
              <span className="text-sm font-medium text-green-400">{stat.change}</span>
            </div>
            <h3 className="text-2xl font-bold text-white mb-1">{stat.value}</h3>
            <p className="text-sm text-gray-400">{stat.label}</p>
          </motion.div>
        ))}
      </div>

      {/* Usage Trend */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="bg-dark-800/50 backdrop-blur-sm border border-white/10 rounded-xl p-6"
      >
        <h2 className="text-xl font-bold text-white mb-6">Usage Trends</h2>
        <ResponsiveContainer width="100%" height={300}>
          <AreaChart data={usageData}>
            <defs>
              <linearGradient id="colorQueries" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#0ea5e9" stopOpacity={0.3}/>
                <stop offset="95%" stopColor="#0ea5e9" stopOpacity={0}/>
              </linearGradient>
              <linearGradient id="colorApiCalls" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#d946ef" stopOpacity={0.3}/>
                <stop offset="95%" stopColor="#d946ef" stopOpacity={0}/>
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#ffffff10" />
            <XAxis dataKey="date" stroke="#9ca3af" />
            <YAxis stroke="#9ca3af" />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            <Area
              type="monotone"
              dataKey="queries"
              stroke="#0ea5e9"
              fillOpacity={1}
              fill="url(#colorQueries)"
              name="Queries"
            />
            <Area
              type="monotone"
              dataKey="apiCalls"
              stroke="#d946ef"
              fillOpacity={1}
              fill="url(#colorApiCalls)"
              name="API Calls"
            />
          </AreaChart>
        </ResponsiveContainer>
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Performance Chart */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-dark-800/50 backdrop-blur-sm border border-white/10 rounded-xl p-6"
        >
          <h2 className="text-xl font-bold text-white mb-6">Response Time (24h)</h2>
          <ResponsiveContainer width="100%" height={250}>
            <LineChart data={performanceData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#ffffff10" />
              <XAxis dataKey="time" stroke="#9ca3af" />
              <YAxis stroke="#9ca3af" />
              <Tooltip content={<CustomTooltip />} />
              <Line
                type="monotone"
                dataKey="responseTime"
                stroke="#10b981"
                strokeWidth={2}
                dot={{ fill: '#10b981', r: 4 }}
                name="Response Time (ms)"
              />
            </LineChart>
          </ResponsiveContainer>
        </motion.div>

        {/* Endpoint Usage */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="bg-dark-800/50 backdrop-blur-sm border border-white/10 rounded-xl p-6"
        >
          <h2 className="text-xl font-bold text-white mb-6">Top Endpoints</h2>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={endpointUsage} layout="horizontal">
              <CartesianGrid strokeDasharray="3 3" stroke="#ffffff10" />
              <XAxis type="number" stroke="#9ca3af" />
              <YAxis type="category" dataKey="endpoint" stroke="#9ca3af" width={80} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="calls" fill="#0ea5e9" name="API Calls" radius={[0, 8, 8, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </motion.div>
      </div>
    </div>
  );
}

export default Analytics;
